import React, { useCallback, useState } from 'react';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useStagesQuery,
  usePerformancesQuery,
  useSaveStageMutation,
  useSavePerformanceMutation,
  useRemovePerformanceMutation,
} from '@/queries/stages';
import { usePerformersQuery } from '@/queries/performers';
import { FestivalHeader, Loading, LocationMapField } from '../controls';
import FilterableTable from '../table';
import Portal from '../modal/Portal';
import { StageFormContainer } from './';

function buildColumnDefinitions() {
  const columnDefs = {};
  columnDefs.stageName = {
    header: 'Stage',
    filterable: true,
    sortable: true,
    showViewLink: false,
    handleClick: true,
  };
  columnDefs.performers = {
    header: 'Performers',
    filterable: false,
    sortable: false,
  };
  return columnDefs;
}

export function Stages() {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const stagesQ = useStagesQuery(festivalId);
  const perfQ = usePerformancesQuery(festivalId);
  const performersQ = usePerformersQuery();

  const saveStageMutation = useSaveStageMutation(festivalId);
  const savePerformanceMutation = useSavePerformanceMutation(festivalId);
  const removePerformanceMutation = useRemovePerformanceMutation(festivalId);

  const stages = stagesQ.data ?? [];
  const performances = perfQ.data ?? [];
  const performers = performersQ.data ?? [];

  const stagesLoading =
    festivalId == null ||
    stagesQ.isPending ||
    stagesQ.isFetching ||
    perfQ.isPending ||
    perfQ.isFetching;
  const performersLoading = performersQ.isPending || performersQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState({});

  const onFileUpload = useCallback((fileInfo) => {
    const { url, filename } = fileInfo;
    setSelectedStage((prev) => ({
      ...prev,
      photoURL: url,
      stagePhoto: filename,
    }));
  }, []);

  const onFileDelete = useCallback(() => {
    setSelectedStage((prev) => ({
      ...prev,
      photoURL: null,
      stagePhoto: null,
    }));
  }, []);

  const openStageModal = useCallback(
    (rowOrEvent) => {
      const row =
        rowOrEvent && typeof rowOrEvent.stageId !== 'undefined'
          ? rowOrEvent
          : null;
      const selected =
        row && stages.some((s) => s.stageId === row.stageId) ? row : {};
      setSelectedStage(selected);
      setShowModal(true);
    },
    [stages],
  );

  const handleDateChange = useCallback((dates) => {
    setSelectedStage((prev) => ({ ...prev, times: dates }));
  }, []);

  const handleLocationChange = useCallback((location) => {
    setSelectedStage((prev) => {
      const updatedLocation = {
        ...prev.location,
        ...location,
        radius: 50,
      };
      return { ...prev, location: updatedLocation };
    });
  }, []);

  const handleLocationNameChange = useCallback((locationName) => {
    setSelectedStage((prev) => ({ ...prev, locationName }));
  }, []);

  const handleStageSave = useCallback(
    async (stage) => {
      const updatedStage = { ...selectedStage, ...stage };
      delete updatedStage.performances;
      delete updatedStage.performers;

      try {
        await saveStageMutation.mutateAsync(updatedStage);
        setShowModal(false);
        setSelectedStage({});
      } catch {
        /* saved via mutation error toast */
      }
    },
    [selectedStage, saveStageMutation],
  );

  const handlePerformanceSave = useCallback(
    (performance) => savePerformanceMutation.mutateAsync(performance),
    [savePerformanceMutation],
  );

  const handlePerformanceDelete = useCallback(
    (performance) =>
      removePerformanceMutation.mutateAsync(performance.performanceId),
    [removePerformanceMutation],
  );

  const getPerformerList = useCallback((stagePerformances) => {
    if (!stagePerformances) {
      return '';
    }

    const performersStr = stagePerformances
      .reduce(
        (list, performance) =>
          list + `${performance.performer.performerName}, `,
        '',
      )
      .trim()
      .slice(0, -1);

    return performersStr;
  }, []);

  const buildViewLink = useCallback((stage) => `/stage/${stage.stageId}`, []);

  const dictOfPerformances = performances.reduce((dict, performance) => {
    if (!dict[performance.stageId]) {
      dict[performance.stageId] = [performance];
    } else {
      dict[performance.stageId].push(performance);
    }

    return dict;
  }, {});

  const populatedStages = stages.map((stage) => ({
    ...stage,
    performances: dictOfPerformances[stage.stageId],
    performers: getPerformerList(dictOfPerformances[stage.stageId]),
  }));

  return (
    <>
      <FestivalHeader
        title="Stages"
        showExit={true}
        showAdd={true}
        addTitle="Add Stage"
        addAction={openStageModal}
      />
      <div className="p-8">
        {stagesLoading || performersLoading ? (
          <Loading />
        ) : (
          <FilterableTable
            data={populatedStages}
            tableTitle=""
            columns={buildColumnDefinitions()}
            includeFilter={false}
            viewLinkBuilder={buildViewLink}
            editHandler={openStageModal}
            toggleModal={() => {
              setShowModal(true);
              setSelectedStage({});
            }}
          />
        )}
      </div>

      <Portal
        header={
          selectedStage
            ? selectedStage.stageName || 'Add New Stage'
            : 'Add New Stage'
        }
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <StageFormContainer
          stage={selectedStage}
          onStageSave={handleStageSave}
          onPerformanceSave={handlePerformanceSave}
          onPerformanceDelete={handlePerformanceDelete}
          onDateChange={handleDateChange}
          onLocationChange={handleLocationChange}
          onLocationNameChange={handleLocationNameChange}
          performers={performers}
          onFileUpload={onFileUpload}
          onFileDelete={onFileDelete}
        />
      </Portal>
    </>
  );
}
